import { useState } from "react";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { Mail, MapPin, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { useSubmitContact } from "@workspace/api-client";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { JsonLd } from "@/components/JsonLd";

const formSchema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Valid email is required"),
  phone: z.string().optional(),
  company: z.string().optional(),
  subject: z.string().min(2, "Subject is required"),
  service: z.string().min(1, "Please select a service"),
  budget: z.string().min(1, "Please select a budget range"),
  timeline: z.string().min(1, "Please select a timeline"),
  message: z.string().min(10, "Message must be at least 10 characters"),
  gdpr: z.boolean().refine(val => val === true, "You must agree to the privacy policy"),
  honeypot: z.string().optional()
});

export default function Contact() {
  const [isSuccess, setIsSuccess] = useState(false);
  const submitContact = useSubmitContact();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "", email: "", phone: "", company: "", subject: "",
      service: "", budget: "", timeline: "", message: "", gdpr: false, honeypot: ""
    }
  });

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    if (data.honeypot) return; // Spam bot protection
    
    submitContact.mutate({ data }, {
      onSuccess: () => setIsSuccess(true),
      onError: (err) => {
        console.error("Contact submission failed:", err);
        toast.error("Something went wrong. Please try again or email us directly at hello@acceleratebyte.com.");
      }
    });
  };

  return (
    <div className="flex flex-col w-full overflow-hidden">
      <SEO
        title="Contact Us | Start Your Project"
        description="Ready to accelerate your technical roadmap? Contact our elite engineering team for a technical scoping call. We respond within 24 hours."
      />
      <JsonLd schema={{
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "name": "Contact AccelerateByte",
        "url": "https://acceleratebyte.com/contact",
        "description": "Ready to accelerate your technical roadmap? Contact our elite engineering team for a technical scoping call. We respond within 24 hours.",
        "mainEntity": { "@id": "https://acceleratebyte.com/#organization" },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://acceleratebyte.com" },
            { "@type": "ListItem", "position": 2, "name": "Contact", "item": "https://acceleratebyte.com/contact" }
          ]
        }
      }} />
      <section className="pt-32 pb-24 px-4">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h1 className="font-display text-5xl md:text-7xl font-bold mb-6">Let's Talk Speed.</h1>
            <p className="text-xl text-muted-foreground">
              Ready to accelerate your technical roadmap? Fill out the form below and a senior engineer will get back to you within 24 hours.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-8">
              <div className="bg-card border border-border/50 rounded-2xl p-8">
                <h3 className="font-display text-2xl font-bold mb-6">Contact Details</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <Mail className="h-6 w-6 text-primary mt-1" />
                    <div>
                      <p className="font-medium text-foreground">Email Us</p>
                      <a href="mailto:hello@acceleratebyte.com" className="text-muted-foreground hover:text-primary transition-colors">hello@acceleratebyte.com</a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <Clock className="h-6 w-6 text-primary mt-1" />
                    <div>
                      <p className="font-medium text-foreground">Response Time</p>
                      <p className="text-muted-foreground">Within 24 hours</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <MapPin className="h-6 w-6 text-primary mt-1" />
                    <div>
                      <p className="font-medium text-foreground">Location</p>
                      <p className="text-muted-foreground">Mumbai, India</p>
                      <p className="text-sm text-muted-foreground mt-1">Serving clients in UK, US, EU, UAE, India</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-card border border-border/50 rounded-2xl p-8 md:p-10">
                {isSuccess ? (
                  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center justify-center text-center py-16">
                    <div className="h-20 w-20 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mb-6">
                      <CheckCircle2 className="h-10 w-10" />
                    </div>
                    <h3 className="font-display text-3xl font-bold mb-4">Request Received</h3>
                    <p className="text-muted-foreground max-w-md mx-auto">
                      Thank you for reaching out. A senior engineer will review your requirements and respond within 24 hours.
                    </p>
                  </motion.div>
                ) : (
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                      {/* Honeypot */}
                      <div className="hidden"><input {...form.register("honeypot")} tabIndex={-1} autoComplete="off" /></div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <FormField control={form.control} name="name" render={({ field }) => (
                          <FormItem><FormLabel>Full Name *</FormLabel><FormControl><Input placeholder="John Doe" {...field} /></FormControl><FormMessage /></FormItem>
                        )} />
                        <FormField control={form.control} name="email" render={({ field }) => (
                          <FormItem><FormLabel>Email Address *</FormLabel><FormControl><Input placeholder="john@company.com" {...field} /></FormControl><FormMessage /></FormItem>
                        )} />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <FormField control={form.control} name="phone" render={({ field }) => (
                          <FormItem><FormLabel>Phone Number</FormLabel><FormControl><Input placeholder="+1 (555) 000-0000" {...field} /></FormControl><FormMessage /></FormItem>
                        )} />
                        <FormField control={form.control} name="company" render={({ field }) => (
                          <FormItem><FormLabel>Company Name</FormLabel><FormControl><Input placeholder="Acme Corp" {...field} /></FormControl><FormMessage /></FormItem>
                        )} />
                      </div>

                      <FormField control={form.control} name="subject" render={({ field }) => (
                        <FormItem><FormLabel>Subject *</FormLabel><FormControl><Input placeholder="Project Inquiry" {...field} /></FormControl><FormMessage /></FormItem>
                      )} />

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <FormField control={form.control} name="service" render={({ field }) => (
                          <FormItem>
                            <FormLabel>Service Required *</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl><SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger></FormControl>
                              <SelectContent>
                                <SelectItem value="website-development">Website Development</SelectItem>
                                <SelectItem value="mobile-app-development">Mobile App Dev</SelectItem>
                                <SelectItem value="mvp-launch">MVP Launch</SelectItem>
                                <SelectItem value="ui-ux-redesign">UI/UX Redesign</SelectItem>
                                <SelectItem value="not-sure">Not sure yet</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )} />
                        
                        <FormField control={form.control} name="budget" render={({ field }) => (
                          <FormItem>
                            <FormLabel>Estimated Budget *</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl><SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger></FormControl>
                              <SelectContent>
                                <SelectItem value="under-500">Under $500</SelectItem>
                                <SelectItem value="500-2k">$500 – $2k</SelectItem>
                                <SelectItem value="2k-5k">$2k – $5k</SelectItem>
                                <SelectItem value="5k-15k">$5k – $15k</SelectItem>
                                <SelectItem value="15k-plus">$15k+</SelectItem>
                                <SelectItem value="discuss">Let's discuss</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )} />

                        <FormField control={form.control} name="timeline" render={({ field }) => (
                          <FormItem>
                            <FormLabel>Timeline *</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl><SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger></FormControl>
                              <SelectContent>
                                <SelectItem value="asap">ASAP</SelectItem>
                                <SelectItem value="1-month">1 month</SelectItem>
                                <SelectItem value="2-3-months">2–3 months</SelectItem>
                                <SelectItem value="3-plus-months">3+ months</SelectItem>
                                <SelectItem value="exploring">Just exploring</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )} />
                      </div>

                      <FormField control={form.control} name="message" render={({ field }) => (
                        <FormItem><FormLabel>Project Details *</FormLabel><FormControl><Textarea placeholder="Tell us about your goals, current challenges, and what you're looking to achieve..." className="min-h-[150px]" {...field} /></FormControl><FormMessage /></FormItem>
                      )} />

                      <FormField control={form.control} name="gdpr" render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-4 border border-border/50 rounded-md bg-muted/20">
                          <FormControl><Checkbox checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="text-sm font-normal text-muted-foreground cursor-pointer">
                              I agree to the <Link href="/privacy" className="text-primary hover:underline">Privacy Policy</Link> and consent to AccelerateByte processing my data to respond to this inquiry.
                            </FormLabel>
                          </div>
                        </FormItem>
                      )} />

                      <Button type="submit" disabled={submitContact.isPending} className="w-full h-12 text-lg font-bold bg-gradient-to-r from-blue-700 to-purple-700 hover:opacity-90 transition-all shadow-lg shadow-primary/20">
                        {submitContact.isPending ? "Sending..." : "Send Request"}
                      </Button>
                    </form>
                  </Form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
